import type { CandlePoint, LivelinePoint } from '@morfi/chart';
import { isMarketResolved } from '@morfi/contract-sdk';
import type { ChartInterval } from '@/lib/chart/types';

const TERMINAL_CHART_OPENING_MAX_SECONDS: number = 24 * 60 * 60;

export interface TerminalChartLifecycle {
  readonly locked: boolean;
  readonly interval: ChartInterval;
  readonly renderMode: 'line' | 'candle';
  readonly resolutionUnix: number | undefined;
}

/** Derives terminal chart behavior without mutating persisted chart settings. */
export function resolveTerminalChartLifecycle(input: {
  readonly status: string | undefined;
  readonly resolvedAt: string | null | undefined;
  readonly persistedInterval: ChartInterval;
  readonly persistedRenderMode: 'line' | 'candle';
  readonly readOnly: boolean;
}): TerminalChartLifecycle {
  const locked = isMarketResolved(input.status ?? '');
  return {
    locked,
    interval: input.readOnly || locked ? 'all' : input.persistedInterval,
    renderMode: input.readOnly || locked ? 'line' : input.persistedRenderMode,
    // Market Share ends at consensus. The graduated winner's later AMM history
    // belongs to its Price and Market Cap charts, not this terminal market chart.
    resolutionUnix: locked ? parseResolutionUnix(input.resolvedAt) : undefined,
  };
}

export function isTerminalChartStatus(status: string | undefined): boolean {
  return isMarketResolved(status ?? '');
}

/** Terminal charts never tween between provisional and final full-history windows. */
export function resolveTerminalChartSnapKey(input: {
  readonly locked: boolean;
  readonly seriesKey: string;
  readonly windowSecs: number;
}): string {
  return input.locked ? `${input.seriesKey}|window:${input.windowSecs}` : input.seriesKey;
}

/**
 * Preserves a terminal chart's full history when its market metadata was not
 * available on the render that established the canvas viewport. The all-history
 * response supplies a trustworthy lower bound in that case.
 */
export function resolveTerminalChartWindowSecs(input: {
  readonly locked: boolean;
  readonly anchorUnix: number;
  readonly marketCreatedAtUnix: number | undefined;
  readonly historyStartUnix: number | undefined;
  readonly fallbackWindowSecs: number;
}): number {
  if (!input.locked) return input.fallbackWindowSecs;

  const validStart = (value: number | undefined): number | undefined =>
    value !== undefined && Number.isFinite(value) && value > 0 && value <= input.anchorUnix
      ? value
      : undefined;
  const startUnix = validStart(input.marketCreatedAtUnix) ?? validStart(input.historyStartUnix);
  return startUnix === undefined
    ? input.fallbackWindowSecs
    : Math.max(60, input.anchorUnix - startUnix);
}

interface TerminalChartData {
  readonly line: ReadonlyArray<LivelinePoint>;
  /** TWAR timestamps are emitted instants; candle-derived lines are bucket starts. */
  readonly lineTimeKind?: 'sample';
  readonly leftEdgeValue?: number;
  readonly ohlcv?: ReadonlyArray<CandlePoint>;
  /** Width of each backend bucket. Required to reject a bucket that crosses resolution. */
  readonly bucketSeconds?: number;
}

/** Bounds terminal history and supplies the value held back to the market's creation edge. */
export function clipTerminalChartDataAt(
  chartData: Readonly<Record<number, TerminalChartData>>,
  resolutionUnix: number | undefined,
  marketCreatedAtUnix?: number,
): Record<number, TerminalChartData> {
  if (resolutionUnix === undefined) return { ...chartData };

  const clipped: Record<number, TerminalChartData> = {};
  for (const [outcomeIndex, entry] of Object.entries(chartData)) {
    const lineToResolution = entry.line.filter((point) =>
      entry.lineTimeKind === 'sample'
        ? point.time <= resolutionUnix
        : bucketEndsAtOrBefore(point.time, entry.bucketSeconds, resolutionUnix),
    );
    const openingPoint =
      marketCreatedAtUnix === undefined
        ? undefined
        : lineToResolution.filter((point) => point.time <= marketCreatedAtUnix).at(-1);
    const openingValue = openingPoint?.value ?? entry.leftEdgeValue;
    const line =
      marketCreatedAtUnix === undefined || openingValue === undefined
        ? lineToResolution
        : [
            { time: marketCreatedAtUnix, value: openingValue },
            ...lineToResolution.filter((point) => point.time > marketCreatedAtUnix),
          ];
    clipped[Number(outcomeIndex)] = {
      ...entry,
      line,
      ...(openingValue !== undefined
        ? { leftEdgeValue: openingValue }
        : entry.leftEdgeValue === undefined && line[0] !== undefined
          ? { leftEdgeValue: line[0].value }
          : {}),
      ...(entry.ohlcv === undefined
        ? {}
        : {
            ohlcv: entry.ohlcv.filter(
              (point) =>
                bucketEndsAtOrBefore(point.time, entry.bucketSeconds, resolutionUnix) &&
                (marketCreatedAtUnix === undefined || point.time >= marketCreatedAtUnix),
            ),
          }),
    };
  }
  return clipped;
}

/**
 * The all-history endpoint adaptively coarsens long-lived charts. Preserve the
 * opening curve at minute resolution without exceeding that endpoint's 24-hour
 * 1m request limit.
 */
export function resolveTerminalChartOpeningEndUnix(input: {
  readonly marketCreatedAtUnix: number | undefined;
  readonly resolvedAtUnix: number | undefined;
}): number | undefined {
  const { marketCreatedAtUnix, resolvedAtUnix } = input;
  if (
    marketCreatedAtUnix === undefined ||
    resolvedAtUnix === undefined ||
    !Number.isFinite(marketCreatedAtUnix) ||
    !Number.isFinite(resolvedAtUnix) ||
    resolvedAtUnix < marketCreatedAtUnix
  ) {
    return undefined;
  }
  return Math.min(resolvedAtUnix, marketCreatedAtUnix + TERMINAL_CHART_OPENING_MAX_SECONDS);
}

/** Replaces the coarse opening bucket with its truthful minute-history points. */
export function mergeTerminalChartOpeningPoints(
  allHistory: ReadonlyArray<{ time: number; value: number }>,
  openingHistory: ReadonlyArray<{ time: number; value: number }>,
): ReadonlyArray<{ time: number; value: number }> {
  const openingEndUnix = openingHistory.at(-1)?.time;
  if (openingEndUnix === undefined || !Number.isFinite(openingEndUnix)) return allHistory;
  return [...openingHistory, ...allHistory.filter((point) => point.time > openingEndUnix)];
}

function parseResolutionUnix(value: string | null | undefined): number | undefined {
  if (typeof value !== 'string' || value === '') return undefined;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp / 1000 : undefined;
}

function bucketEndsAtOrBefore(
  bucketStartUnix: number,
  bucketSeconds: number | undefined,
  resolutionUnix: number,
): boolean {
  if (!Number.isInteger(bucketSeconds) || bucketSeconds === undefined || bucketSeconds <= 0) {
    return bucketStartUnix <= resolutionUnix;
  }
  return bucketStartUnix + bucketSeconds <= resolutionUnix;
}
